/**
 * Expression-based Validation Mode
 * 
 * User answers are validated by evaluating a boolean expression.
 * Variables in the expression are bound to user inputs.
 */
import { ValidatedExerciseJSON } from '../transformers/json-schema';
import { ValidationResult, WidgetValidation } from './types';
import { evaluate, extractVariables } from './evaluator';

/**
 * Validate using expression mode
 *
 * @param exercise - The exercise JSON (with validation present)
 * @param userInputs - User's answers keyed by widget ID (variable names)
 * @returns Validation result
 */
export function validateExpression(
  exercise: ValidatedExerciseJSON,
  userInputs: Record<string, string | number>
): ValidationResult {
  const details: Record<string, WidgetValidation> = {};
  const { expression, variables } = exercise.validation;

  // Validate that we have an expression
  if (!expression) {
    return {
      isCorrect: false,
      details: {},
      error: 'No expression defined for validation'
    };
  }

  // Get valid variable names from the exercise widgets (these are the actual input names)
  const validVariableNames = Object.keys(exercise.widgets);

  // Get variables from the expression (use provided list or extract from expression, filtered by valid names)
  const expectedVariables = variables ?? extractVariables(expression, validVariableNames);

  // Build the scope for evaluation
  const scope: Record<string, number> = {};
  let hasInvalidInput = false;

  for (const varName of expectedVariables) {
    const rawValue = userInputs[varName];
    
    // Check if input exists
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      details[varName] = {
        widgetId: varName,
        userValue: rawValue,
        isCorrect: false,
        error: 'No input provided'
      };
      hasInvalidInput = true;
      continue;
    }

    // Convert to number (supports fraction strings like "5/3")
    const numValue = typeof rawValue === 'number'
      ? rawValue
      : parseFractionOrNumber(String(rawValue).trim());

    if (!Number.isFinite(numValue)) {
      details[varName] = {
        widgetId: varName,
        userValue: rawValue,
        isCorrect: false,
        error: 'Invalid number'
      };
      hasInvalidInput = true;
      continue;
    }

    // Valid input
    scope[varName] = numValue;
    details[varName] = {
      widgetId: varName,
      userValue: rawValue,
      isCorrect: true // Will be updated based on expression result
    };
  }

  // If any input is invalid, the whole exercise is incorrect
  if (hasInvalidInput) {
    return {
      isCorrect: false,
      details,
      error: 'Invalid input values'
    };
  }

  // Evaluate the expression
  try {
    const result = evaluate(expression, scope);

    // Update all widget validations based on overall result
    // In expression mode, we can't determine which individual widget is "wrong"
    // The expression either passes or fails as a whole
    for (const varName of expectedVariables) {
      if (details[varName]) {
        details[varName].isCorrect = result;
      }
    }

    return {
      isCorrect: result,
      details,
      expression,
      scope
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    // Mark all as incorrect on evaluation error
    for (const varName of expectedVariables) {
      if (details[varName]) {
        details[varName].isCorrect = false;
      }
    }

    return {
      isCorrect: false,
      details,
      error: `Evaluation error: ${errorMessage}`
    };
  }
}

/**
 * Check if any combination of values can satisfy the expression
 * (useful for debugging/testing exercises)
 *
 * @param expression - The expression to check
 * @param ranges - Value ranges for each variable
 * @returns First valid combination found, or null
 */
export function findValidCombination(
  expression: string,
  ranges: Record<string, { min: number; max: number; step?: number }>
): Record<string, number> | null {
  // Extract variables, filtering by the keys in ranges (these are the valid variable names)
  const validVariableNames = Object.keys(ranges);
  const variables = extractVariables(expression, validVariableNames);
  
  // Build value arrays for each variable
  const valueArrays: number[][] = [];
  const varNames: string[] = [];

  for (const varName of variables) {
    const range = ranges[varName] ?? { min: 1, max: 100, step: 1 };
    const values: number[] = [];
    for (let v = range.min; v <= range.max; v += (range.step ?? 1)) {
      values.push(v);
    }
    valueArrays.push(values);
    varNames.push(varName);
  }

  // Try all combinations (brute force, for small ranges only)
  const maxIterations = 10000;
  let iterations = 0;

  function* combinations(arrays: number[][], prefix: number[] = []): Generator<number[]> {
    if (arrays.length === 0) {
      yield prefix;
      return;
    }
    const [first, ...rest] = arrays;
    for (const value of first!) {
      yield* combinations(rest, [...prefix, value]);
    }
  }

  for (const combo of combinations(valueArrays)) {
    if (iterations++ > maxIterations) break;

    const scope: Record<string, number> = {};
    for (let i = 0; i < varNames.length; i++) {
      scope[varNames[i]!] = combo[i]!;
    }

    try {
      if (evaluate(expression, scope)) {
        return scope;
      }
    } catch {
      continue;
    }
  }

  return null;
}

/**
 * Parse a value that may be a plain number ("5") or a fraction string ("5/3").
 * Returns NaN if neither format is valid.
 */
function parseFractionOrNumber(s: string): number {
  const parts = s.split('/');
  if (parts.length === 2) {
    const num = parseFloat(parts[0]!);
    const den = parseFloat(parts[1]!);
    if (Number.isFinite(num) && Number.isFinite(den) && den !== 0) {
      return num / den;
    }
    return NaN;
  }
  return parseFloat(s);
}
